import { useState } from "react";

import { ENV } from "@/env";
import { AdminLayout } from "@/components/admin/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, Database, KeyRound, ShieldCheck } from "lucide-react";

const SQL = `-- ---------- BLOG (usar schema public) ----------
create table if not exists public.blog_posts (
  id uuid primary key default gen_random_uuid(),
  slug text unique not null,
  title text not null,
  excerpt text,
  content_md text,
  cover_url text,
  seo_title text,
  seo_description text,
  status text not null check (status in ('draft','published','archived')) default 'draft',
  author text,
  published_at timestamptz,
  created_at timestamptz default now(),
  updated_at timestamptz default now()
);

-- RLS
alter table public.blog_posts enable row level security;

do $$
begin
  if not exists (select 1 from pg_policies where polname='blog_read_published') then
    create policy "blog_read_published" on public.blog_posts
    for select using (status = 'published');
  end if;
end $$;

-- Opcional: trigger de updated_at
create or replace function public.set_updated_at() returns trigger as $$
begin new.updated_at = now(); return new; end; $$ language plpgsql;

drop trigger if exists blog_posts_set_updated_at on public.blog_posts;
create trigger blog_posts_set_updated_at
before update on public.blog_posts
for each row execute procedure public.set_updated_at();

-- ---------- STORAGE ----------
-- Crie o bucket 'blog' no Storage (público)
-- Storage → New Bucket → blog (Public)`;

export default function Health() {
  const [copyStatus, setCopyStatus] = useState<"idle" | "success" | "error">("idle");

  async function copySQL() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(SQL);
      } else if (typeof globalThis !== "undefined" && globalThis.document) {
        const textarea = globalThis.document.createElement("textarea");
        textarea.value = SQL;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        globalThis.document.body.appendChild(textarea);
        textarea.select();
        globalThis.document.execCommand("copy");
        globalThis.document.body.removeChild(textarea);
      }
      setCopyStatus("success");
      setTimeout(() => setCopyStatus("idle"), 3000);
    } catch (error) {
      console.error("Erro ao copiar SQL", error);
      setCopyStatus("error");
    }
  }
  const checks = [
    { k: "VITE_SUPABASE_URL", ok: Boolean(ENV.SUPABASE_URL), usingDefault: ENV.USING_DEFAULT_SUPABASE },
    { k: "VITE_SUPABASE_ANON_KEY", ok: Boolean(ENV.SUPABASE_ANON), usingDefault: ENV.USING_DEFAULT_SUPABASE },
  ];
  return (
    <AdminLayout
      title="Status do ecossistema"
      description="Monitore integrações críticas, variáveis de ambiente e scripts essenciais para o funcionamento do painel."
      breadcrumbs={[
        { label: "Admin", href: "/admin" },
        { label: "Infraestrutura" },
        { label: "Health" },
      ]}
      status={{ tone: "neutral", label: "Monitoramento manual recomendado" }}
    >
      <section className="grid gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader className="flex flex-col gap-2 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">Variáveis de ambiente</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Confirme se as credenciais do Supabase estão configuradas para produção.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {checks.map(check => (
              <div
                key={check.k}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FF5605]/10 text-[#FF5605]">
                    <KeyRound className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-semibold text-slate-900">{check.k}</p>
                    <p className="text-xs text-slate-500">{check.ok ? "Configuração detectada" : "Ajuste necessário"}</p>
                  </div>
                </div>
                <Badge className={check.ok ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}>
                  {check.ok ? "OK" : "Faltando"}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Checklist rápido</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Passos essenciais para manter o painel seguro e atualizado.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-xs text-slate-600">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <ShieldCheck className="h-4 w-4 text-[#3332FC]" /> Autenticação Google
              </h4>
              <p className="mt-1">Ative o provedor Google no Supabase e cadastre as URLs de redirecionamento listadas abaixo.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Database className="h-4 w-4 text-[#FF5605]" /> Tabela do blog
              </h4>
              <p className="mt-1">Execute o SQL sugerido para criar a estrutura de posts e políticas de segurança.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Bucket público
              </h4>
              <p className="mt-1">Crie o bucket <code>blog</code> no Storage do Supabase com acesso público para armazenar capas.</p>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader className="flex flex-col gap-2 pb-4">
            <CardTitle className="text-lg text-slate-900">Script SQL recomendado</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Copie e execute no SQL Editor do Supabase para provisionar a tabela do blog.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={copySQL} className="bg-emerald-500 text-emerald-950 hover:bg-emerald-400">
              Copiar SQL de criação (blog_posts + RLS)
            </Button>
            <p
              aria-live="polite"
              className={
                copyStatus === "error"
                  ? "text-xs text-rose-600"
                  : copyStatus === "success"
                    ? "text-xs text-emerald-600"
                    : "text-xs text-slate-600"
              }
            >
              {copyStatus === "success" && "SQL copiado! Cole no Supabase (SQL Editor) e execute."}
              {copyStatus === "error" && "Não foi possível copiar automaticamente. Copie manualmente do código abaixo."}
              {copyStatus === "idle" && "Use o botão para copiar o SQL necessário para criar a tabela do blog."}
            </p>
            <pre className="max-h-[420px] overflow-x-auto overflow-y-auto rounded-2xl border border-slate-200 bg-slate-900 p-4 text-xs text-emerald-100">
{SQL}
            </pre>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">URLs essenciais</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Garanta que os redirecionamentos e ambientes estejam autorizados no Supabase.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-slate-600">
            <div>
              <h4 className="font-semibold text-slate-900">Redirect URLs</h4>
              <ul className="mt-1 space-y-1">
                <li>http://localhost:5173</li>
                <li>https://toctoc-site.lovable.app</li>
                <li>https://toctoc-site.vercel.app</li>
              </ul>
            </div>
            <Separator className="bg-slate-200" />
            <div className="space-y-1">
              <h4 className="font-semibold text-slate-900">Dicas rápidas</h4>
              <p>Revise a política RLS após executar o script e valide se o bucket <code>blog</code> está público.</p>
            </div>
          </CardContent>
        </Card>
      </section>
    </AdminLayout>
  );
}
